import React, { useMemo, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { WorkshopMultiDetailDTO, WorkshopSessionBody, WorkshopSessionDTO } from "../../../../services/v2/workshops.service";
import useEsTelefono from "../../../../utils/esTelefono";
import { fechaHoraAmPm } from "../../../../utils/fechaHoraAmPm";
import S from "./strings";
import { Modal, Opciones, errorText, horaBonita } from "./comun";
import { FechaDMY } from "../../../../components/fechas/camposFecha";
import DiasEditor, { DiaDraft, HoraSelect, diaValido, horasEntre } from "./dias";

/** "2026-11-07" -> "07/11/2026" */
export const fechaCorta = (d: string | null | undefined): string => fechaHoraAmPm(String(d || "").substring(0, 10));

/** "2026-11-07" -> "07/11" (chips de la lista de inscritas). */
export const diaMes = (d: string | null | undefined): string => fechaCorta(d).substring(0, 5);

/** "10:00" + "14:30" -> "10:00 am a 2:30 pm" */
export const horario = (x: { startTime: string; endTime: string | null }): string =>
	horaBonita(x.startTime) + (x.endTime ? " a " + horaBonita(x.endTime) : "");

const hoy = (): string => {
	const d = new Date();
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const masUnDia = (iso: string): string => {
	const [y, m, d] = iso.split("-").map(Number);
	const x = new Date(y, m - 1, d + 1);
	return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};

/** Alta / edición de una sesión. */
const SesionForm: React.FC<{ campusId: number; taller: WorkshopMultiDetailDTO; sesion: WorkshopSessionDTO | null; opciones: Opciones;
	onClose: () => void; onSaved: (t: WorkshopMultiDetailDTO, texto: string) => void }> = ({ campusId, taller, sesion, opciones, onClose, onSaved }) => {
	const http = HttpManager.getInstance();
	//Una sesión nueva copia la última (día siguiente, mismas horas, aula y maestra); sin sesiones, sale del taller.
	const previa = (taller.sessions || [])[(taller.sessions || []).length - 1] || null;
	const inicioTaller = String(taller.startsAt || "");
	const finTaller = String(taller.endsAt || "");
	const [f, setF] = useState(() => sesion ? {
		date: sesion.date, startTime: sesion.startTime, endTime: sesion.endTime || "",
		classroomId: sesion.classroomId || 0, mentorId: sesion.mentorId || 0, notes: sesion.notes || ""
	} : previa ? {
		date: masUnDia(previa.date), startTime: previa.startTime, endTime: previa.endTime || "",
		classroomId: previa.classroomId || 0, mentorId: previa.mentorId || 0, notes: ""
	} : {
		date: inicioTaller.substring(0, 10) || hoy(),
		startTime: inicioTaller.length > 10 && inicioTaller.substring(11, 16) !== "00:00" ? inicioTaller.substring(11, 16) : "",
		endTime: finTaller.substring(0, 10) === inicioTaller.substring(0, 10) && finTaller.length > 10 ? finTaller.substring(11, 16) : "",
		classroomId: taller.classroomId || 0, mentorId: taller.mentorId || 0, notes: ""
	});
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const set = (k: keyof typeof f, v: any) => setF(x => ({ ...x, [k]: v }));
	const valido = /^\d{4}-\d{2}-\d{2}$/.test(f.date) && f.startTime !== "" && horasEntre(f.startTime, f.endTime) > 0;

	const aulas = opciones.aulas.map(a => ({ id: a.id, name: a.buildingName ? `${a.name}, ${a.buildingName}` : a.name }));
	if (f.classroomId && !aulas.some(a => a.id === f.classroomId)) aulas.unshift({ id: f.classroomId, name: sesion?.classroomName || taller.classroomName || String(f.classroomId) });
	const maestras = opciones.maestras.map(m => ({ id: m.id, name: m.name }));
	if (f.mentorId && !maestras.some(m => m.id === f.mentorId)) maestras.unshift({ id: f.mentorId, name: sesion?.mentorName || taller.mentorName || String(f.mentorId) });

	const guardar = () => {
		const body: WorkshopSessionBody = {
			date: f.date, startTime: f.startTime, endTime: f.endTime || null,
			classroomId: f.classroomId || null, mentorId: f.mentorId || null, notes: f.notes.trim() || null
		};
		setBusy(true); setError(null);
		const req = sesion ? http.workshopsService.updateSession(campusId, sesion.id, body) : http.workshopsService.addSession(campusId, taller.id, body);
		req.then(r => onSaved(r.data, sesion ? S.sesionGuardada : S.sesionAgregada))
			.catch(e => { setError(errorText(e)); setBusy(false); });
	};

	return (
		<Modal title={sesion ? S.editarSesion : S.nuevaSesion} onClose={onClose} ancho={600}>
			<div className="wau-fila wte-fila">
				<div className="wau-campo" style={{ flex: "1 1 160px" }}><span className="wau-lab">{S.fecha}</span>
					<FechaDMY className="wau-entrada wau-num" value={f.date} onChange={v => set("date", v)} /></div>
				<div className="wau-campo" style={{ flex: "1 1 140px" }}><span className="wau-lab">{S.entrada}</span>
					<HoraSelect value={f.startTime} onChange={v => set("startTime", v)} label={S.entrada} /></div>
				<div className="wau-campo" style={{ flex: "1 1 140px" }}><span className="wau-lab">{S.salida}</span>
					<HoraSelect value={f.endTime} onChange={v => set("endTime", v)} label={S.salida} /></div>
			</div>
			<div className="wau-fila wte-fila">
				<label className="wau-campo" style={{ flex: "1 1 200px" }}><span className="wau-lab">{S.aula}</span>
					<select className="wau-entrada" value={f.classroomId} onChange={e => set("classroomId", Number(e.target.value))}>
						<option value={0}>{S.ninguna}</option>
						{aulas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
					</select></label>
				<label className="wau-campo" style={{ flex: "1 1 200px" }}><span className="wau-lab">{S.maestra}</span>
					<select className="wau-entrada" value={f.mentorId} onChange={e => set("mentorId", Number(e.target.value))}>
						<option value={0}>{S.ninguna}</option>
						{maestras.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
					</select></label>
			</div>
			<label className="wau-campo"><span className="wau-lab">{S.notas}</span>
				<textarea className="wau-entrada" rows={2} value={f.notes} onChange={e => set("notes", e.target.value)} /></label>
			{error ? <p className="wau-adv" role="alert" style={{ marginTop: 8 }}>{error}</p> : null}
			<div className="wau-acciones">
				<button type="button" className="wau-btn p" onClick={guardar} disabled={busy || !valido}>{sesion ? S.guardar : S.agregarSesion}</button>
			</div>
		</Modal>
	);
};

/** Agregar varios días a un taller (v2, 2026-10-23): mismo calendario que al crearlo; los días nuevos copian la última sesión. */
const AgregarDias: React.FC<{ campusId: number; taller: WorkshopMultiDetailDTO; opciones: Opciones; onClose: () => void;
	onSaved: (t: WorkshopMultiDetailDTO, n: number) => void }> = ({ campusId, taller, opciones, onClose, onSaved }) => {
	const http = HttpManager.getInstance();
	const ss = taller.sessions || [];
	const previa = ss[ss.length - 1] || null;
	const [dias, setDias] = useState<DiaDraft[]>([]);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const valido = dias.length > 0 && dias.every(diaValido);
	const guardar = () => {
		setBusy(true); setError(null);
		http.workshopsService.addSessions(campusId, taller.id, dias.map(d => ({ date: d.date, startTime: d.startTime, endTime: d.endTime || null, classroomId: d.classroomId || null, mentorId: d.mentorId || null })))
			.then(r => onSaved(r.data, dias.length))
			.catch(e => { setError(errorText(e)); setBusy(false); });
	};
	return (
		<Modal title={S.agregarDias} onClose={onClose} ancho={1120}>
			<DiasEditor value={dias} onChange={setDias} opciones={opciones} ocupados={ss.map(x => x.date)}
				base={previa ? { date: previa.date, startTime: previa.startTime, endTime: previa.endTime || "", classroomId: previa.classroomId || 0, mentorId: previa.mentorId || 0 }
					: { date: String(taller.startsAt || "").substring(0, 10) || undefined }} />
			{error ? <p className="wau-adv" role="alert" style={{ marginTop: 8 }}>{error}</p> : null}
			<div className="wau-acciones">
				<button type="button" className="wau-btn p" onClick={guardar} disabled={busy || !valido}>{S.agregarDias}</button>
			</div>
		</Modal>
	);
};

/** Pase de lista de una sesión: cada inscrita vigente, presente o ausente (PUT _sessionAttendance). */
const AsistenciaSesion: React.FC<{ campusId: number; taller: WorkshopMultiDetailDTO; sesionId: number; soloLectura: boolean;
	onChange: (t: WorkshopMultiDetailDTO) => void; onClose: () => void }> = ({ campusId, taller, sesionId, soloLectura, onChange, onClose }) => {
	const http = HttpManager.getInstance();
	const [error, setError] = useState<string | null>(null);
	const [enCurso, setEnCurso] = useState<number[]>([]);
	const sesion = (taller.sessions || []).find(x => x.id === sesionId);
	const vigentes = taller.attendees.filter(a => a.status === 1);
	if (!sesion) return null;
	const pasada = sesion.date <= hoy();
	const marcar = (attendeeId: number, v: boolean) => {
		setError(null);
		setEnCurso(x => [...x, attendeeId]);
		//Se pinta al instante; la respuesta trae el detalle completo y lo reemplaza.
		onChange({ ...taller, attendees: taller.attendees.map(a => a.id !== attendeeId ? a : { ...a, sessions: (a.sessions || []).map(s => s.sessionId === sesionId ? { ...s, attended: v } : s) }) });
		http.workshopsService.setSessionAttendance(campusId, sesionId, attendeeId, v)
			.then(r => onChange(r.data))
			.catch(e => {
				setError(errorText(e));
				http.workshopsService.details(campusId, taller.id).then(r => onChange(r.data as WorkshopMultiDetailDTO)).catch(() => { /* se queda lo pintado */ });
			})
			.finally(() => setEnCurso(x => x.filter(y => y !== attendeeId)));
	};
	const presentes = vigentes.filter(a => (a.sessions || []).some(s => s.sessionId === sesionId && s.attended)).length;
	return (
		<Modal title={S.asistencia} onClose={onClose} ancho={560}>
			<div className="wte-pase-cab">
				<span><b className="wau-num">{fechaCorta(sesion.date)}</b> <span className="wau-sec wau-num">{horario(sesion)}</span></span>
				<b className="wau-num">{presentes} / {vigentes.length}</b>
			</div>
			{vigentes.length === 0 ? <p className="wau-sec">{S.sinInscritas}</p> : (
				<div className="wte-pase">
					{vigentes.map(a => {
						const v = (a.sessions || []).some(s => s.sessionId === sesionId && s.attended);
						return (
							<label key={a.id} className={"wte-pase-fila" + (v ? " on" : "")}>
								<input type="checkbox" checked={v} disabled={soloLectura || enCurso.includes(a.id)} onChange={e => marcar(a.id, e.target.checked)} />
								<span className="nom"><b>{a.studentName}</b><span className="wau-sec">{a.identityCode}</span></span>
								<span className={"est" + (v ? " si" : pasada ? " wau-oro" : " wau-sec")}>{v ? S.presente : S.ausente}</span>
							</label>
						);
					})}
				</div>
			)}
			{error ? <p className="wau-adv" role="alert" style={{ marginTop: 8 }}>{error}</p> : null}
		</Modal>
	);
};

/** Caja "Sesiones" del detalle: lista, alta, edición, borrado y pase de lista por sesión. */
export const Sesiones: React.FC<{ campusId: number; taller: WorkshopMultiDetailDTO; opciones: Opciones; soloLectura: boolean;
	onChange: (t: WorkshopMultiDetailDTO) => void; notify: (text: string, ok?: boolean) => void }> = ({ campusId, taller, opciones, soloLectura, onChange, notify }) => {
	const http = HttpManager.getInstance();
	const movil = useEsTelefono();
	const [form, setForm] = useState<WorkshopSessionDTO | null>(null);
	const [agregar, setAgregar] = useState<boolean>(false);
	const [pase, setPase] = useState<number | null>(null);
	const [borrar, setBorrar] = useState<WorkshopSessionDTO | null>(null);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const sesiones = useMemo(() => taller.sessions || [], [taller]);

	const confirmarBorrar = () => {
		if (!borrar) return;
		setBusy(true); setError(null);
		http.workshopsService.deleteSession(campusId, borrar.id)
			.then(r => { onChange(r.data); setBorrar(null); notify(S.sesionBorrada); })
			.catch(e => setError(errorText(e)))
			.finally(() => setBusy(false));
	};
	const lugar = (x: WorkshopSessionDTO) => [x.classroomName, x.mentorName].filter(Boolean);
	const botonPase = (x: WorkshopSessionDTO) => (
		<button type="button" className="wau-btn s mini wau-num" style={{ whiteSpace: "nowrap" }} onClick={() => setPase(x.id)}
			aria-label={S.pasarLista + " " + fechaCorta(x.date)}>{S.pasarLista} · {x.attendance.attended} / {x.attendance.total}</button>
	);
	const acciones = (x: WorkshopSessionDTO) => soloLectura ? null : (
		<>
			<button type="button" className="wau-btn s mini" onClick={() => setForm(x)}>{S.editar}</button>
			<button type="button" className="wau-btn s mini" onClick={() => { setError(null); setBorrar(x); }}>{S.borrar}</button>
		</>
	);

	return (
		<div className="wau-caja">
			<div className="wte-cab-caja">
				<h3>{S.sesiones} <span className="wau-sec wau-num">({sesiones.length})</span></h3>
				{soloLectura ? null : <button type="button" className="wau-btn p mini" onClick={() => setAgregar(true)}>{S.agregarDias}</button>}
			</div>
			{sesiones.length === 0 ? <p className="wau-sec" style={{ margin: 0 }}>{S.sinSesiones}</p> : movil ? (
				<div className="wte-ses-lista">
					{sesiones.map(x => (
						<div key={x.id} className="wau-tarjeta">
							<div><b className="wau-num">{fechaCorta(x.date)}</b> <span className="wau-num">{horario(x)}</span></div>
							{lugar(x).length ? <div className="linea">{lugar(x).join(", ")}</div> : null}
							{x.notes ? <div className="linea">{x.notes}</div> : null}
							<div className="wau-acciones" style={{ justifyContent: "flex-start", marginTop: 8 }}>{botonPase(x)}{acciones(x)}</div>
						</div>
					))}
				</div>
			) : (
				<div className="wau-tabla">
					<table className="wte-ses-tabla">
						<thead><tr><th>{S.colFecha}</th><th>{S.colHorario}</th><th>{S.aula}</th><th>{S.maestra}</th><th>{S.colAsistencia}</th><th></th></tr></thead>
						<tbody>
							{sesiones.map(x => (
								<tr key={x.id}>
									<td className="wau-num" style={{ whiteSpace: "nowrap" }}>{fechaCorta(x.date)}</td>
									<td className="wau-num">{horario(x)}{x.notes ? <div className="wau-sec" style={{ fontSize: 12.5 }}>{x.notes}</div> : null}</td>
									<td>{x.classroomName || "—"}</td>
									<td>{x.mentorName || "—"}</td>
									<td>{botonPase(x)}</td>
									<td className="der" style={{ whiteSpace: "nowrap" }}>{acciones(x)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{form ? <SesionForm campusId={campusId} taller={taller} opciones={opciones} sesion={form} onClose={() => setForm(null)}
				onSaved={(t, texto) => { onChange(t); setForm(null); notify(texto); }} /> : null}
			{agregar ? <AgregarDias campusId={campusId} taller={taller} opciones={opciones} onClose={() => setAgregar(false)}
				onSaved={(t, n) => { onChange(t); setAgregar(false); notify(S.diasAgregados(n)); }} /> : null}
			{pase !== null ? <AsistenciaSesion campusId={campusId} taller={taller} sesionId={pase} soloLectura={soloLectura} onChange={onChange} onClose={() => setPase(null)} /> : null}
			{borrar ? (
				<Modal title={S.borrarSesion} onClose={() => !busy && setBorrar(null)} ancho={440}>
					<p className="wau-num" style={{ margin: 0 }}><b>{fechaCorta(borrar.date)}</b> {horario(borrar)}</p>
					{error ? <p className="wau-adv" role="alert" style={{ marginTop: 8 }}>{error}</p> : null}
					<div className="wau-acciones">
						<button type="button" className="wau-btn p" onClick={confirmarBorrar} disabled={busy}>{S.borrar}</button>
					</div>
				</Modal>
			) : null}
		</div>
	);
};

/** Columna "Sesiones" de la lista de inscritas: un chip por sesión; presionarlo marca o quita la asistencia. */
export const ChipsSesiones: React.FC<{ campusId: number; taller: WorkshopMultiDetailDTO; attendeeId: number; editable: boolean;
	onChange: (t: WorkshopMultiDetailDTO) => void; notify: (text: string, ok?: boolean) => void }> = ({ campusId, taller, attendeeId, editable, onChange, notify }) => {
	const http = HttpManager.getInstance();
	const a = taller.attendees.find(x => x.id === attendeeId);
	const marcas = new Map((a?.sessions || []).map(s => [s.sessionId, s.attended]));
	const marcar = (sesionId: number, v: boolean) => {
		onChange({ ...taller, attendees: taller.attendees.map(x => x.id !== attendeeId ? x : { ...x, sessions: (x.sessions || []).map(s => s.sessionId === sesionId ? { ...s, attended: v } : s) }) });
		http.workshopsService.setSessionAttendance(campusId, sesionId, attendeeId, v)
			.then(r => onChange(r.data))
			.catch(e => { notify(errorText(e), false); http.workshopsService.details(campusId, taller.id).then(r => onChange(r.data as WorkshopMultiDetailDTO)).catch(() => { /* nada */ }); });
	};
	return (
		<div className="wte-ses-chips">
			{(taller.sessions || []).map(x => {
				const v = !!marcas.get(x.id);
				const titulo = fechaCorta(x.date) + " " + horario(x) + ": " + (v ? S.presente : S.ausente);
				return editable
					? <button key={x.id} type="button" className={"wte-ses wau-num" + (v ? " on" : "")} aria-pressed={v} title={titulo} aria-label={titulo} onClick={() => marcar(x.id, !v)}>{diaMes(x.date)}</button>
					: <span key={x.id} className={"wte-ses wau-num" + (v ? " on" : "")} title={titulo}>{diaMes(x.date)}</span>;
			})}
		</div>
	);
};
